import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, Badge, Button, Alert, Modal } from '../components/design-system';
import { MapView } from '../components/MapView';
import {
  ChevronRight, MapPin, Clock, DollarSign, Phone, Star,
  Navigation, CheckCircle, Truck, Package, ArrowLeft,
} from 'lucide-react';
import courierApi from '../services/courierApi';

export const ContractsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadContracts();
  }, [filter]);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await courierApi.listContracts(params);
      setContracts(res.contracts || []);
    } catch (err) {
      console.error('Failed to load contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    active: 'primary',
    pickup_enroute: 'info',
    picked_up: 'info',
    in_transit: 'warning',
    delivered: 'success',
    completed: 'success',
    cancelled: 'danger',
  };

  const filters = [
    { value: 'all', label: 'All' },
    { value: 'active', label: t('contract.status.active') },
    { value: 'in_transit', label: t('contract.status.in_transit') },
    { value: 'completed', label: t('contract.status.completed') },
  ];

  return (
    <div className="p-4 pb-2">
      <h1 className="text-lg font-bold text-gray-900 mb-4">{t('nav.contracts')}</h1>

      <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filter === f.value
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-3 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-12">
          <Truck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{t('home.noActiveContracts')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map((contract) => (
            <Card key={contract.id} padding="sm" className="hover:border-primary-300 transition-colors">
              <button
                onClick={() => navigate(`/contracts/${contract.id}`)}
                className="w-full text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <Truck className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {contract.requestTitle}
                      </p>
                      <Badge variant={statusColors[contract.status] || 'default'} size="sm">
                        {t(`contract.status.${contract.status}`)}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">
                      {contract.providerName} &bull; {contract.vehicleType}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {contract.feeAmount} {contract.feeCurrency}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(contract.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export const ContractDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [providerLocation, setProviderLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [offers, setOffers] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    loadContract();
  }, [id]);

  useEffect(() => {
    if (!contract) return;
    const trackingStatuses = ['active', 'pickup_enroute', 'picked_up', 'in_transit'];
    if (!trackingStatuses.includes(contract.status)) return;

    const interval = setInterval(async () => {
      try {
        const res = await courierApi.getLatestLocation(contract.id);
        if (res.location) setProviderLocation(res.location);
      } catch {}
    }, 10000);

    // Initial fetch
    courierApi.getLatestLocation(contract.id)
      .then(res => { if (res.location) setProviderLocation(res.location); })
      .catch(() => {});

    return () => clearInterval(interval);
  }, [contract?.id, contract?.status]);

  const loadContract = async () => {
    setLoading(true);
    try {
      const res = await courierApi.getContract(id);
      setContract(res.contract);

      if (res.contract.requestId) {
        try {
          const offersRes = await courierApi.listOffers({ requestId: res.contract.requestId });
          setOffers(offersRes.offers || []);
        } catch {}
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setActionLoading(true);
    setError('');
    try {
      await courierApi.updateContractStatus(id, newStatus);
      await loadContract();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelivery = async () => {
    setActionLoading(true);
    setError('');
    try {
      await courierApi.confirmDelivery(id);
      await loadContract();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-3 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500">{t('common.error')}</p>
      </div>
    );
  }

  const isCustomer = String(contract.customerUserId) === String(user?.id) || String(Math.trunc(Number(contract.customerUserId))) === String(user?.id);
  const isProvider = String(contract.providerUserId) === String(user?.id) || String(Math.trunc(Number(contract.providerUserId))) === String(user?.id);
  const isTracking = ['pickup_enroute', 'picked_up', 'in_transit'].includes(contract.status);

  const statusColors = {
    active: 'primary', pickup_enroute: 'info', picked_up: 'info',
    in_transit: 'warning', delivered: 'success', completed: 'success', cancelled: 'danger',
  };

  const mapMarkers = [];
  if (contract.pickupLatitude != null && contract.pickupLongitude != null) {
    mapMarkers.push({ latitude: contract.pickupLatitude, longitude: contract.pickupLongitude, type: 'pickup', label: t('map.pickup') });
  }
  if (contract.dropoffLatitude != null && contract.dropoffLongitude != null) {
    mapMarkers.push({ latitude: contract.dropoffLatitude, longitude: contract.dropoffLongitude, type: 'dropoff', label: t('map.dropoff') });
  }

  return (
    <div className="flex flex-col h-[calc(100vh-7.5rem)]">
      {/* Map for tracking */}
      {isTracking && (
        <div className="h-48 relative">
          <MapView
            className="w-full h-full"
            markers={mapMarkers}
            providerLocation={providerLocation}
            center={providerLocation ? [providerLocation.latitude, providerLocation.longitude] : null}
          />
          {providerLocation && (
            <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-md text-xs z-[1000] flex items-center gap-2">
              <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse-dot" />
              {t('contract.providerEnRoute')}
            </div>
          )}
        </div>
      )}

      {/* Contract details */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <button onClick={() => navigate('/contracts')} className="flex items-center gap-1 text-sm text-gray-600 mb-2">
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>

        {error && (
          <Alert variant="error" dismissible onDismiss={() => setError('')}>{error}</Alert>
        )}

        <Card padding="sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900">{contract.requestTitle}</h2>
            <Badge variant={statusColors[contract.status]} size="sm">
              {t(`contract.status.${contract.status}`)}
            </Badge>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>{contract.pickupAddress}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="h-4 w-4 text-red-500 flex-shrink-0" />
              <span>{contract.dropoffAddress}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <DollarSign className="h-4 w-4 flex-shrink-0 text-green-500" />
              <span><span className="font-semibold text-gray-900">{contract.feeAmount} {contract.feeCurrency}</span> {t('offer.agreedFee')} &bull; {contract.paymentMethod}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Truck className="h-4 w-4 flex-shrink-0" />
              <span>{contract.providerName} &bull; {contract.vehicleType}</span>
            </div>
            {contract.providerPhone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <a href={`tel:${contract.providerPhone}`} className="text-primary-600">{contract.providerPhone}</a>
              </div>
            )}
          </div>
        </Card>

        {/* Action buttons */}
        <div className="space-y-2">
          {isProvider && contract.status === 'active' && (
            <Button fullWidth onClick={() => updateStatus('pickup_enroute')} loading={actionLoading}>
              <Navigation className="h-4 w-4" />
              {t('contract.startTransport')}
            </Button>
          )}
          {isProvider && contract.status === 'pickup_enroute' && (
            <Button fullWidth onClick={() => updateStatus('picked_up')} loading={actionLoading}>
              <CheckCircle className="h-4 w-4" />
              {t('contract.markPickedUp')}
            </Button>
          )}
          {isProvider && contract.status === 'picked_up' && (
            <Button fullWidth onClick={() => updateStatus('in_transit')} loading={actionLoading}>
              <Truck className="h-4 w-4" />
              {t('contract.markInTransit')}
            </Button>
          )}
          {isProvider && contract.status === 'in_transit' && (
            <Button fullWidth onClick={() => updateStatus('delivered')} loading={actionLoading}>
              <Package className="h-4 w-4" />
              {t('contract.markDelivered')}
            </Button>
          )}
          {contract.status === 'delivered' && (
            <div className="space-y-2">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-sm font-semibold text-blue-900 mb-2">{t('contract.mutualConfirmation')}</p>
                <div className="flex items-center gap-2 text-xs mb-1">
                  <div className={`h-4 w-4 rounded-full flex items-center justify-center ${contract.deliveryConfirmedProvider ? 'bg-green-500' : 'bg-gray-300'}`}>
                    {contract.deliveryConfirmedProvider && <CheckCircle className="h-3 w-3 text-white" />}
                  </div>
                  <span className={contract.deliveryConfirmedProvider ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    {t('contract.providerConfirmed')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className={`h-4 w-4 rounded-full flex items-center justify-center ${contract.deliveryConfirmedCustomer ? 'bg-green-500' : 'bg-gray-300'}`}>
                    {contract.deliveryConfirmedCustomer && <CheckCircle className="h-3 w-3 text-white" />}
                  </div>
                  <span className={contract.deliveryConfirmedCustomer ? 'text-green-700 font-medium' : 'text-gray-500'}>
                    {t('contract.customerConfirmed')}
                  </span>
                </div>
              </div>
              {((isCustomer && !contract.deliveryConfirmedCustomer) || (isProvider && !contract.deliveryConfirmedProvider)) && (
                <Button fullWidth onClick={confirmDelivery} loading={actionLoading}>
                  <CheckCircle className="h-4 w-4" />
                  {t('contract.confirmDelivery')}
                </Button>
              )}
              {((isCustomer && contract.deliveryConfirmedCustomer) || (isProvider && contract.deliveryConfirmedProvider)) && (
                <p className="text-xs text-center text-gray-500">{t('contract.waitingOtherParty')}</p>
              )}
            </div>
          )}
          {contract.status === 'completed' && (
            <Button fullWidth variant="outline" onClick={() => setShowReviewModal(true)}>
              <Star className="h-4 w-4" />
              {t('review.leaveReview')}
            </Button>
          )}
          {['active', 'pickup_enroute'].includes(contract.status) && (
            <Button fullWidth variant="danger" onClick={() => updateStatus('cancelled')} loading={actionLoading}>
              {t('contract.cancelContract')}
            </Button>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          contractId={contract.id}
          role={isCustomer ? 'provider' : 'customer'}
          onSubmitted={() => {
            setShowReviewModal(false);
          }}
        />
      )}
    </div>
  );
};

const ReviewModal = ({ isOpen, onClose, contractId, role, onSubmitted }) => {
  const { t } = useTranslation();
  const [npsScore, setNpsScore] = useState(8);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await courierApi.createReview({
        contractId,
        npsScore,
        comment: comment || null,
      });
      onSubmitted();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getNpsLabel = (score) => {
    if (score >= 9) return { label: t('review.npsLabels.promoter'), color: 'text-green-600' };
    if (score >= 7) return { label: t('review.npsLabels.passive'), color: 'text-yellow-600' };
    return { label: t('review.npsLabels.detractor'), color: 'text-red-600' };
  };

  const npsInfo = getNpsLabel(npsScore);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('review.leaveReview')} size="sm">
      {error && (
        <Alert variant="error" className="mb-4" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('review.npsDescription', { role })}
          </label>
          <div className="flex items-center gap-1 mb-2">
            {[...Array(11)].map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setNpsScore(i)}
                className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${
                  i === npsScore
                    ? 'bg-primary-500 text-white scale-110'
                    : i <= npsScore
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
          <p className={`text-xs font-medium ${npsInfo.color}`}>{npsInfo.label}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('review.comment')}
          </label>
          <textarea
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            rows={3}
            placeholder={t('review.commentPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <Button type="submit" fullWidth loading={loading} size="lg">
          {t('review.submitReview')}
        </Button>
      </form>
    </Modal>
  );
};
